import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { updateHreflangTags } from '../../utils/hreflang';

const faqItems = [
  {
    question: "Do you perform transplant surgeries?",
    answer: "No. All medical procedures are conducted exclusively by licensed hospitals and qualified transplant surgeons. We provide coordination and information support only."
  },
  {
    question: "Do you make medical decisions for patients?",
    answer: "No. All medical decisions are made by licensed physicians in consultation with the patient. We provide general information and coordination support only."
  },
  {
    question: "Do you guarantee transplant success?",
    answer: "No. We do not guarantee transplant outcomes, specific timelines, or medical results. Outcomes depend on individual medical factors determined by treating physicians."
  },
  {
    question: "How long does the process take?",
    answer: "The timeline varies significantly depending on medical readiness, donor availability, and the country of treatment. It may take weeks to months. Your treating physician will provide a personalized timeline."
  },
  {
    question: "What is your role in the process?",
    answer: "We provide general information about transplant pathways, facilitate communication with hospitals upon request, offer transparent cost guidance, and support logistical preparation. All medical procedures and decisions are handled by licensed healthcare providers."
  },
  {
    question: "Can I contact the hospital directly?",
    answer: "Yes. Patients communicate directly with hospital representatives. We facilitate communication when requested, but all medical advice and treatment decisions are made by licensed physicians, not by us."
  }
];

const steps = [
  {
    number: 1,
    title: "Initial Information Request",
    description: "The process begins when a patient submits an inquiry seeking general information about overseas kidney transplant options. Before starting, patients may find it helpful to review our <Link to=\"/decision-checklist\" className=\"text-teal-600 hover:text-teal-700 font-medium\">decision checklist</Link> to ensure they have considered all key factors.",
    details: [
      "Patient submits inquiry form or contacts us directly",
      "We provide general information about transplant pathways",
      "Overview of countries, typical timelines, and cost ranges",
      "Explanation of what to expect in the process"
    ],
    note: "We provide informational support only. We do not make medical judgments or determine eligibility.",
    icon: "ri-file-list-3-line"
  },
  {
    number: 2,
    title: "Medical Record Review (By Hospital)",
    description: "Medical evaluation is conducted by licensed transplant specialists at the treating hospital, not by us.",
    details: [
      "Patient submits medical records to the hospital",
      "Licensed physicians review medical history and test results",
      "Hospital determines transplant eligibility",
      "Medical team assesses individual suitability"
    ],
    note: "All medical evaluations and eligibility determinations are made exclusively by licensed physicians. We do not make medical judgments.",
    icon: "ri-health-book-line"
  },
  {
    number: 3,
    title: "Direct Communication with Hospital",
    description: "Patients communicate directly with hospital representatives to discuss medical details, treatment plans, and next steps.",
    details: [
      "Hospital provides medical assessment results",
      "Physicians explain treatment options and risks",
      "Patients ask medical questions directly to doctors",
      "Final medical decisions are made by physicians and patients"
    ],
    note: "We facilitate communication when requested, but all medical advice and treatment decisions are made by licensed physicians, not by us.",
    icon: "ri-hospital-line"
  },
  {
    number: 4,
    title: "Logistical Preparation",
    description: "Once medical decisions are made by the treating physicians, we assist with logistical coordination (if requested).",
    details: [
      "Travel coordination assistance (flights, accommodation)",
      "Documentation preparation support (visa, medical records)",
      "Pre-departure checklist and guidance",
      "Communication facilitation with hospital staff"
    ],
    note: "This is coordination support only. All medical preparation and pre-surgery instructions are provided by the treating hospital.",
    icon: "ri-calendar-check-line"
  },
  {
    number: 5,
    title: "Treatment & Post-Procedure Follow-Up",
    description: "All medical procedures are conducted exclusively by licensed hospitals and qualified transplant surgeons.",
    details: [
      "Surgery performed by qualified transplant surgeons",
      "Hospital stay and post-operative monitoring",
      "Immunosuppressive medication management by medical team",
      "Post-care instructions provided by treating physicians"
    ],
    note: "We do not perform medical procedures or provide medical treatment. All surgery and post-operative care are managed by licensed healthcare providers.",
    icon: "ri-heart-pulse-line"
  }
];

export default function ProcessPage() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [openFaqIndex, setOpenFaqIndex] = useState<number | null>(null);

  useEffect(() => {
    document.title = 'International Kidney Transplant Process | Coordination Steps';

    const updateMetaTag = (name: string, content: string, isProperty = false) => {
      const attribute = isProperty ? 'property' : 'name';
      let element = document.querySelector(`meta[${attribute}="${name}"]`);
      if (!element) {
        element = document.createElement('meta');
        element.setAttribute(attribute, name);
        document.head.appendChild(element);
      }
      element.setAttribute('content', content);
    };

    const updateLinkTag = (rel: string, href: string) => {
      let element = document.querySelector(`link[rel="${rel}"]`) as HTMLLinkElement;
      if (!element) {
        element = document.createElement('link');
        element.setAttribute('rel', rel);
        document.head.appendChild(element);
      }
      element.href = href;
    };

    updateMetaTag('description', 'Step-by-step overview of the international kidney transplant coordination process. Learn how we facilitate communication between patients and licensed hospitals abroad.');
    updateMetaTag('keywords', 'kidney transplant process, international transplant coordination steps, medical evaluation kidney, hospital communication, transplant logistics, post-procedure follow-up');
    updateMetaTag('last-modified', new Date().toISOString().split('T')[0]);

    updateLinkTag('canonical', 'https://kidneypatientsupport.com/process');

    // Hreflang tags
    updateHreflangTags({
      enPath: '/process',
      jaPath: '/process' // Japanese process page exists
    });

    updateMetaTag('og:url', 'https://kidneypatientsupport.com/process', true);
    updateMetaTag('og:title', 'How the Process Works | International Kidney Transplant Coordination Support', true);
    updateMetaTag('og:description', 'Step-by-step overview of the international kidney transplant coordination process. We provide information and logistical support only. All medical decisions are made by licensed physicians.', true);
    updateMetaTag('og:type', 'website', true);

    updateMetaTag('twitter:url', 'https://kidneypatientsupport.com/process');
    updateMetaTag('twitter:title', 'How the Process Works | International Kidney Transplant Coordination Support');
    updateMetaTag('twitter:description', 'Step-by-step overview of the international kidney transplant coordination process. We provide information and logistical support only. All medical decisions are made by licensed physicians.');
    updateMetaTag('twitter:card', 'summary_large_image');

    const scripts: HTMLScriptElement[] = [];

    const breadcrumbScript = document.createElement('script');
    breadcrumbScript.type = 'application/ld+json';
    breadcrumbScript.text = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      "itemListElement": [
        { "@type": "ListItem", "position": 1, "name": "Home", "item": "https://kidneypatientsupport.com/" },
        { "@type": "ListItem", "position": 2, "name": "Process", "item": "https://kidneypatientsupport.com/process/" }
      ]
    });
    document.head.appendChild(breadcrumbScript);
    scripts.push(breadcrumbScript);

    const webPageScript = document.createElement('script');
    webPageScript.type = 'application/ld+json';
    webPageScript.text = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "WebPage",
      "name": "How the Process Works | International Kidney Transplant Coordination Support",
      "description": "Step-by-step overview of the international kidney transplant coordination process. We provide information and logistical support only. All medical decisions are made by licensed physicians.",
      "url": "https://kidneypatientsupport.com/process/",
      "inLanguage": "en",
      "isPartOf": { "@type": "WebSite", "@id": "https://kidneypatientsupport.com/#website" },
      "publisher": { "@id": "https://kidneypatientsupport.com/#organization" }
    });
    document.head.appendChild(webPageScript);
    scripts.push(webPageScript);

    const faqScript = document.createElement('script');
    faqScript.type = 'application/ld+json';
    faqScript.text = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "FAQPage",
      "mainEntity": faqItems.map(item => ({
        "@type": "Question",
        "name": item.question,
        "acceptedAnswer": { "@type": "Answer", "text": item.answer }
      }))
    });
    document.head.appendChild(faqScript);
    scripts.push(faqScript);

    const handleScroll = () => setIsScrolled(window.scrollY > 50);
    window.addEventListener('scroll', handleScroll);

    return () => {
      window.removeEventListener('scroll', handleScroll);
      scripts.forEach(script => {
        if (script.parentNode) document.head.removeChild(script);
      });
    };
  }, []);

  return (
    <div className="min-h-screen bg-white font-['Inter',sans-serif]">
      {/* Navigation */}
      <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${isScrolled ? 'bg-white shadow-md' : 'bg-white/95 backdrop-blur-sm'}`}>
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center cursor-pointer">
            <img src="https://static.readdy.ai/image/523aa923c70fc5f766f2f9fe4af6dd5e/f1b13f3c5cdde047774c7297e509c16c.png" alt="Kidney Patient Support" className="h-12 w-auto" />
          </Link>

          <div className="hidden lg:flex items-center space-x-8">
            <Link to="/" className="text-sm font-medium text-gray-700 hover:text-teal-600 transition-colors whitespace-nowrap cursor-pointer">Home</Link>
            <Link to="/ethics" className="text-sm font-medium text-gray-700 hover:text-teal-600 transition-colors whitespace-nowrap cursor-pointer">Ethics</Link>
            <Link to="/complete-guide-kidney-transplant-abroad" className="text-sm font-medium text-gray-700 hover:text-teal-600 transition-colors whitespace-nowrap cursor-pointer">Guide</Link>
            <Link to="/countries" className="text-sm font-medium text-gray-700 hover:text-teal-600 transition-colors whitespace-nowrap cursor-pointer">Countries</Link>
            <Link to="/pricing" className="text-sm font-medium text-gray-700 hover:text-teal-600 transition-colors whitespace-nowrap cursor-pointer">Pricing</Link>
            <Link to="/process" className="text-sm font-medium text-teal-600 transition-colors whitespace-nowrap cursor-pointer">Process</Link>
            <Link to="/contact" className="text-sm font-medium text-gray-700 hover:text-teal-600 transition-colors whitespace-nowrap cursor-pointer">Contact</Link>
          </div>

          <button
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            className="lg:hidden w-10 h-10 flex items-center justify-center text-gray-700 hover:text-teal-600 transition-colors cursor-pointer z-[60]"
            aria-label="Toggle menu"
          >
            {isMobileMenuOpen ? <i className="ri-close-line text-2xl"></i> : <i className="ri-menu-line text-2xl"></i>}
          </button>
        </div>

        {isMobileMenuOpen && (
          <div className="lg:hidden fixed inset-0 top-[72px] bg-white z-[55] overflow-y-auto">
            <div className="flex flex-col p-6 space-y-4">
              <Link to="/" onClick={() => setIsMobileMenuOpen(false)} className="text-base font-medium text-gray-700 hover:text-teal-600 transition-colors py-3 border-b border-gray-100 cursor-pointer">Home</Link>
              <Link to="/ethics" onClick={() => setIsMobileMenuOpen(false)} className="text-base font-medium text-gray-700 hover:text-teal-600 transition-colors py-3 border-b border-gray-100 cursor-pointer">Ethics</Link>
              <Link to="/complete-guide-kidney-transplant-abroad" onClick={() => setIsMobileMenuOpen(false)} className="text-base font-medium text-gray-700 hover:text-teal-600 transition-colors py-3 border-b border-gray-100 cursor-pointer">Guide</Link>
              <Link to="/countries" onClick={() => setIsMobileMenuOpen(false)} className="text-base font-medium text-gray-700 hover:text-teal-600 transition-colors py-3 border-b border-gray-100 cursor-pointer">Countries</Link>
              <Link to="/pricing" onClick={() => setIsMobileMenuOpen(false)} className="text-base font-medium text-gray-700 hover:text-teal-600 transition-colors py-3 border-b border-gray-100 cursor-pointer">Pricing</Link>
              <Link to="/process" onClick={() => setIsMobileMenuOpen(false)} className="text-base font-medium text-teal-600 transition-colors py-3 border-b border-gray-100 cursor-pointer">Process</Link>
              <Link to="/contact" onClick={() => setIsMobileMenuOpen(false)} className="text-base font-medium text-gray-700 hover:text-teal-600 transition-colors py-3 border-b border-gray-100 cursor-pointer">Contact</Link>
            </div>
          </div>
        )}
      </nav>

      {/* Hero Section */}
      <section className="relative min-h-[60vh] flex items-center justify-center overflow-hidden pt-20">
        <div className="absolute inset-0">
          <img
            src="https://public.readdy.ai/ai/img_res/17b9ca3115a973728eb4095d20ea6dca.jpg"
            alt="Medical coordination representing kidney transplant process support"
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/55 via-black/35 to-black/55"></div>
        </div>
        <div className="relative max-w-5xl mx-auto px-6 text-center py-28 w-full">
          <div className="inline-block bg-teal-600/20 backdrop-blur-sm border border-teal-400/30 text-white px-6 py-2 rounded-full mb-6">
            <span className="text-sm font-semibold tracking-wide">COORDINATION & INFORMATION SUPPORT</span>
          </div>
          <h1 className="text-5xl md:text-6xl font-bold text-white mb-8 leading-tight">
            International Kidney Transplant Process
          </h1>
          <div className="bg-white/10 backdrop-blur-sm border border-white/30 rounded-2xl p-8 mb-8 max-w-3xl mx-auto">
            <p className="text-xl text-white/95 mb-5 leading-relaxed">
              Understanding the <strong>kidney transplant coordination process</strong> helps patients navigate their journey abroad. We provide information and logistical support while <Link to="/ethics" className="text-teal-300 hover:text-teal-200 underline">licensed physicians make all medical decisions</Link>.
            </p>
          </div>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/contact"
              className="inline-flex items-center justify-center bg-teal-600 text-white px-8 py-4 rounded-full text-base font-medium hover:bg-teal-700 transition-colors space-x-3 whitespace-nowrap cursor-pointer"
            >
              <span>Request Information</span>
              <i className="ri-arrow-right-line"></i>
            </Link>
            <Link
              to="/ethics"
              className="inline-flex items-center justify-center bg-white/20 backdrop-blur-sm border border-white/30 text-white px-8 py-4 rounded-full text-base font-medium hover:bg-white/30 transition-colors space-x-3 whitespace-nowrap cursor-pointer"
            >
              <span>Our Ethical Framework</span>
              <i className="ri-shield-check-line"></i>
            </Link>
          </div>
        </div>
      </section>

      {/* Process Steps Section */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-5xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
              Kidney Transplant Process Steps: <span className="text-teal-600">From Inquiry to Recovery</span>
            </h2>
            <p className="text-gray-700 text-lg max-w-3xl mx-auto leading-relaxed mb-4">
              Understanding the <strong>kidney transplant process steps</strong> helps patients prepare for each phase of their journey abroad. From the initial information request through post-procedure follow-up, each step involves collaboration between patients, our coordination team, and licensed medical professionals. Before starting, patients may find it helpful to review our <Link to="/decision-checklist" className="text-teal-600 hover:text-teal-700 font-medium">decision checklist</Link> to ensure they have considered all key factors.
            </p>
            <p className="text-gray-600 text-base max-w-3xl mx-auto leading-relaxed">
              Understanding the process also helps with financial planning — review our <Link to="/pricing" className="text-teal-600 hover:text-teal-700 font-medium">transparent cost overview</Link> for estimated ranges and payment structure details.
            </p>
          </div>

          <div className="space-y-6">
            {steps.map((step) => (
              <div
                key={step.number}
                className="bg-white border border-gray-200 rounded-3xl p-8 md:p-10 hover:shadow-lg transition-shadow"
              >
                <div className="flex flex-col md:flex-row gap-8">
                  <div className="flex-shrink-0">
                    <div className="w-20 h-20 bg-teal-50 border-2 border-teal-200 rounded-2xl flex items-center justify-center">
                      <i className={`${step.icon} text-teal-600 text-4xl`}></i>
                    </div>
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center gap-4 mb-4">
                      <span className="text-teal-600 text-sm font-bold tracking-wider">STEP {step.number}</span>
                      <div className="h-px flex-1 bg-gray-200"></div>
                    </div>
                    <h3 className="text-2xl md:text-3xl font-bold text-gray-900 mb-4">{step.title}</h3>
                    <p className="text-gray-700 text-lg leading-relaxed mb-4">{step.description}</p>

                    {step.details && (
                      <ul className="space-y-2 mb-4">
                        {step.details.map((detail, idx) => (
                          <li key={idx} className="flex items-start text-gray-700 text-base">
                            <i className="ri-checkbox-circle-fill text-teal-600 mt-1 mr-3 flex-shrink-0"></i>
                            <span>{detail}</span>
                          </li>
                        ))}
                      </ul>
                    )}

                    {step.note && (
                      <div className="bg-orange-50 border-l-4 border-orange-400 rounded-lg p-4 mt-4">
                        <p className="text-gray-700 text-sm leading-relaxed">
                          <i className="ri-information-line text-orange-600 mr-2"></i>
                          <strong>Important:</strong> {step.note}
                        </p>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Important Disclaimer Section */}
      <section className="py-20 bg-yellow-50 border-y border-yellow-200">
        <div className="max-w-5xl mx-auto px-6">
          <div className="text-center">
            <h2 className="text-4xl font-bold text-gray-900 mb-6">Our Role in the Transplant Process</h2>
            <div className="bg-white rounded-3xl p-10 border border-yellow-200">
              <p className="text-gray-700 text-xl leading-relaxed mb-6">
                We provide <strong>coordination and information support only</strong>. All medical procedures are performed by licensed hospitals in accordance with our <Link to="/ethics" className="text-teal-600 hover:text-teal-700 font-medium">ethical framework</Link>.
              </p>
              <div className="space-y-4 max-w-2xl mx-auto">
                <p className="flex items-center justify-center text-gray-700 text-base">
                  <i className="ri-close-circle-line text-red-600 mr-3 flex-shrink-0 text-xl"></i>
                  <span>We do not provide medical advice, diagnosis, or treatment.</span>
                </p>
                <p className="flex items-center justify-center text-gray-700 text-base">
                  <i className="ri-close-circle-line text-red-600 mr-3 flex-shrink-0 text-xl"></i>
                  <span>We do not make medical decisions for patients.</span>
                </p>
                <p className="flex items-center justify-center text-gray-700 text-base">
                  <i className="ri-checkbox-circle-fill text-teal-600 mr-3 flex-shrink-0 text-xl"></i>
                  <span>All medical decisions remain between patient and licensed physicians.</span>
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-20 bg-white">
        <div className="max-w-4xl mx-auto px-6">
          <div className="text-center mb-14">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
              Kidney Transplant Process: <span className="text-teal-600">Common Questions</span>
            </h2>
            <p className="text-gray-700 text-lg leading-relaxed max-w-3xl mx-auto">
              Answers to frequently asked questions about the coordination process and our support role.
            </p>
          </div>

          <div className="space-y-4">
            {faqItems.map((item, index) => (
              <div key={index} className={`bg-gray-50 rounded-2xl border transition-all ${openFaqIndex === index ? 'border-teal-300 shadow-md' : 'border-gray-200'}`}>
                <button
                  onClick={() => setOpenFaqIndex(openFaqIndex === index ? null : index)}
                  className="w-full flex items-center justify-between p-6 text-left cursor-pointer hover:bg-gray-100 transition-colors rounded-2xl"
                >
                  <h3 className="text-lg font-bold text-gray-900 pr-4">{item.question}</h3>
                  <div className="w-8 h-8 flex items-center justify-center flex-shrink-0">
                    <i className={`${openFaqIndex === index ? 'ri-subtract-line' : 'ri-add-line'} text-teal-600 text-xl`}></i>
                  </div>
                </button>
                {openFaqIndex === index && (
                  <div className="px-6 pb-6">
                    <div className="border-t border-gray-200 pt-4">
                      <p className="text-gray-700 text-base leading-relaxed">
                        {item.answer}
                      </p>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-br from-teal-600 to-teal-700 relative overflow-hidden">
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute -top-40 -right-40 w-96 h-96 bg-white rounded-full opacity-10 blur-3xl"></div>
          <div className="absolute -bottom-40 -left-40 w-96 h-96 bg-white rounded-full opacity-10 blur-3xl"></div>
        </div>
        <div className="max-w-5xl mx-auto px-6 text-center relative z-10">
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
            Questions About the Process?
          </h2>
          <p className="text-xl text-white/90 leading-relaxed mb-10 max-w-3xl mx-auto">
            We provide transparent guidance about the <strong>kidney transplant coordination process</strong>. Medical decisions remain with licensed physicians.
          </p>
          <Link to="/contact" className="inline-flex items-center justify-center bg-white text-teal-600 px-12 py-6 rounded-full text-xl font-bold hover:bg-gray-100 transition-colors space-x-3 whitespace-nowrap shadow-2xl cursor-pointer mb-4">
            <span>Request Information</span>
            <i className="ri-arrow-right-line"></i>
          </Link>
          <p className="text-white/80 text-sm">
            Transparent communication. No medical decision-making.
          </p>
        </div>
      </section>

      {/* Low-Friction Micro CTA */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-3xl mx-auto px-6 text-center">
          <p className="text-gray-700 text-xl mb-6">
            Unsure if this is right for you?
          </p>
          <Link 
            to="/who-this-is-for"
            className="inline-flex items-center justify-center bg-white text-teal-600 border-2 border-teal-600 px-8 py-4 rounded-full text-lg font-semibold hover:bg-teal-50 transition-colors space-x-3 whitespace-nowrap cursor-pointer"
          >
            <span>Learn More</span>
            <i className="ri-checkbox-circle-line"></i>
          </Link>
        </div>
      </section>

      {/* What to Expect at Each Stage */}
      <section className="py-20 bg-white">
        <div className="max-w-5xl mx-auto px-6">
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4 text-center">
            What to Expect at Each Stage of the Transplant Process
          </h2>
          <p className="text-gray-700 text-lg text-center mb-10 max-w-3xl mx-auto">
            Patients often want to know what the <strong>kidney transplant process</strong> looks like in practical terms — how long each phase takes, what documentation is needed, and what role they play at each step. Here is additional context to help set realistic expectations.
          </p>

          <div className="space-y-6 mb-10">
            <div className="bg-gradient-to-br from-teal-50 to-emerald-50 rounded-2xl p-8 border border-teal-100">
              <h3 className="text-xl font-bold text-gray-900 mb-3">Preparing Your Medical Records</h3>
              <p className="text-gray-700 text-base leading-relaxed">
                One of the most important early steps in the kidney transplant process is gathering comprehensive medical records. This typically includes recent blood work, dialysis history, cardiac evaluations, and any imaging studies related to your kidney condition. The treating hospital's transplant team uses these records to assess eligibility, so completeness and accuracy directly affect how quickly the evaluation can proceed. We provide guidance on what documents are typically required, but the hospital determines what is medically necessary for their assessment.
              </p>
            </div>

            <div className="bg-gradient-to-br from-teal-50 to-emerald-50 rounded-2xl p-8 border border-teal-100">
              <h3 className="text-xl font-bold text-gray-900 mb-3">Communication Between Patient and Hospital</h3>
              <p className="text-gray-700 text-base leading-relaxed">
                Throughout the transplant process steps, clear communication between the patient and the treating hospital is essential. We facilitate this communication when requested, but patients always have the option to communicate directly with hospital representatives. Questions about medical risks, surgical approach, expected recovery time, and post-operative care should be directed to the treating physicians. Our coordination role ensures that logistical details — scheduling, travel arrangements, documentation — do not create unnecessary barriers to this critical medical dialogue.
              </p>
            </div>

            <div className="bg-gradient-to-br from-teal-50 to-emerald-50 rounded-2xl p-8 border border-teal-100">
              <h3 className="text-xl font-bold text-gray-900 mb-3">After Return: Continuity of Care</h3>
              <p className="text-gray-700 text-base leading-relaxed">
                The transplant process does not end when the patient returns home. Post-transplant care requires regular follow-up with a local nephrologist, ongoing immunosuppressive medication management, and periodic blood tests to monitor kidney function. Before traveling, patients should identify a nephrologist in their home country who is willing to provide post-transplant follow-up care. The treating hospital provides detailed medical records and post-operative instructions to support this continuity. For more on preparing for the return home, review our <Link to="/after-return-preparation" className="text-teal-600 hover:text-teal-700 font-medium">after-return preparation guide</Link>.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Next Steps */}
      <section className="py-20 bg-white">
        <div className="max-w-5xl mx-auto px-6">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">Explore Further</h2>
          </div>
          <div className="grid md:grid-cols-3 gap-6">
            <Link
              to="/who-this-is-for"
              className="group bg-gray-50 rounded-2xl p-6 hover:bg-teal-50 hover:border-teal-200 border border-gray-200 transition-all cursor-pointer"
            >
              <div className="w-12 h-12 bg-teal-100 rounded-xl flex items-center justify-center mb-4 group-hover:bg-teal-600 transition-colors">
                <i className="ri-user-search-line text-teal-600 text-2xl group-hover:text-white"></i>
              </div>
              <h4 className="font-bold text-gray-900 mb-2 text-base">
                Who This Is For
              </h4>
              <p className="text-gray-600 text-sm mb-3">Check if this service is suitable for you</p>
              <span className="text-teal-600 font-semibold text-sm group-hover:text-teal-700">Learn More <i className="ri-arrow-right-line"></i></span>
            </Link>

            <Link
              to="/countries"
              className="group bg-gray-50 rounded-2xl p-6 hover:bg-teal-50 hover:border-teal-200 border border-gray-200 transition-all cursor-pointer"
            >
              <div className="w-12 h-12 bg-teal-100 rounded-xl flex items-center justify-center mb-4 group-hover:bg-teal-600 transition-colors">
                <i className="ri-global-line text-teal-600 text-2xl group-hover:text-white"></i>
              </div>
              <h4 className="font-bold text-gray-900 mb-2 text-base">
                Compare Countries
              </h4>
              <p className="text-gray-600 text-sm mb-3">Review India, Cambodia, and Pakistan programs</p>
              <span className="text-teal-600 font-semibold text-sm group-hover:text-teal-700">View Comparison <i className="ri-arrow-right-line"></i></span>
            </Link>

            <Link
              to="/pricing"
              className="group bg-gray-50 rounded-2xl p-6 hover:bg-teal-50 hover:border-teal-200 border border-gray-200 transition-all cursor-pointer"
            >
              <div className="w-12 h-12 bg-teal-100 rounded-xl flex items-center justify-center mb-4 group-hover:bg-teal-600 transition-colors">
                <i className="ri-money-dollar-circle-line text-teal-600 text-2xl group-hover:text-white"></i>
              </div>
              <h4 className="font-bold text-gray-900 mb-2 text-base">
                Transparent Cost Overview
              </h4>
              <p className="text-gray-600 text-sm mb-3">View estimated costs with full transparency</p>
              <span className="text-teal-600 font-semibold text-sm group-hover:text-teal-700">View Pricing <i className="ri-arrow-right-line"></i></span>
            </Link>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gradient-to-br from-teal-600 to-teal-700 text-white py-12">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-8">
            <p className="text-white/90 text-base leading-relaxed max-w-4xl mx-auto">
              <strong>This website provides coordination and informational support services only.</strong><br />
              Medical decisions and procedures are handled exclusively by licensed healthcare providers.
            </p>
          </div>

          {/* Core Pages */}
          <div className="border-t border-white/20 pt-8 mb-8">
            <h4 className="text-white font-bold text-sm tracking-wide mb-4 text-center">CORE PAGES</h4>
            <div className="flex flex-wrap justify-center gap-x-8 gap-y-2">
              <Link to="/pakistan" className="text-white/80 hover:text-white text-sm transition-colors cursor-pointer">Kidney Transplant in Pakistan</Link>
              <Link to="/pricing" className="text-white/80 hover:text-white text-sm transition-colors cursor-pointer">Transparent Cost Overview</Link>
              <Link to="/process" className="text-white/80 hover:text-white text-sm transition-colors cursor-pointer">How the Process Works</Link>
              <Link to="/ethics" className="text-white/80 hover:text-white text-sm transition-colors cursor-pointer">Ethical Framework</Link>
              <Link to="/who-this-is-for" className="text-white/80 hover:text-white text-sm transition-colors cursor-pointer">Eligibility Guide</Link>
            </div>
          </div>

          <div className="border-t border-white/20 pt-8 flex flex-col md:flex-row justify-between items-center">
            <p className="text-white/80 text-sm mb-4 md:mb-0">
              &copy; 2025 Japan Ambis International. All rights reserved.
            </p>
            <div className="flex space-x-6">
              <Link to="/privacy" className="text-white/80 hover:text-white text-sm transition-colors cursor-pointer">Privacy Policy</Link>
              <Link to="/ethics" className="text-white/80 hover:text-white text-sm transition-colors cursor-pointer">Ethics</Link>
              <a href="https://readdy.ai/?ref=logo" target="_blank" rel="noopener noreferrer" className="text-white/80 hover:text-white text-sm transition-colors cursor-pointer">Powered by Readdy</a>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
